<template>
  <div class="project-assignments-page">
    <!-- Header Card -->
    <b-card class="mb-2">
      <b-row class="align-items-center">
        <b-col
          cols="12"
          md="6"
        >
          <h3 class="mb-0">
            <feather-icon
              icon="BriefcaseIcon"
              size="24"
              class="mr-50"
            />
            مهام 168
          </h3>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="text-md-right mt-1 mt-md-0"
        >
          <!-- Select Projects Button -->
          <!-- <b-button
            variant="primary"
            class="mr-1"
            @click="showSelectProjectsModal = true"
            :disabled="loading"
          >
            <feather-icon icon="PlusIcon" size="16" class="mr-50" />
            <span class="d-none d-sm-inline">اختيار مشاريع</span>
          </b-button> -->

          <!-- Refresh Button -->
          <b-button
            variant="outline-primary"
            :disabled="loading"
            @click="refreshProjects"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'rotate-animation': loading }"
            />
          </b-button>
        </b-col>
      </b-row>

      <!-- Stats Row -->
      <b-row class="mt-2">
        <b-col
          cols="6"
          md="3"
          class="mb-1 mb-md-0"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-warning"
              size="45"
              rounded
            >
              <feather-icon
                icon="ClockIcon"
                size="20"
              />
            </b-avatar>
            <div class="ml-1">
              <h4 class="mb-0">
                {{ getProjectCountByStatus('pending') }}
              </h4>
              <small class="text-muted">قيد الانتظار</small>
            </div>
          </div>
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1 mb-md-0"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-info"
              size="45"
              rounded
            >
              <feather-icon
                icon="PlayIcon"
                size="20"
              />
            </b-avatar>
            <div class="ml-1">
              <h4 class="mb-0">
                {{ getProjectCountByStatus('in_progress') }}
              </h4>
              <small class="text-muted">قيد التنفيذ</small>
            </div>
          </div>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-primary"
              size="45"
              rounded
            >
              <feather-icon
                icon="EyeIcon"
                size="20"
              />
            </b-avatar>
            <div class="ml-1">
              <h4 class="mb-0">
                {{ getProjectCountByStatus('reviewing') }}
              </h4>
              <small class="text-muted">قيد المراجعة</small>
            </div>
          </div>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-success"
              size="45"
              rounded
            >
              <feather-icon
                icon="CheckIcon"
                size="20"
              />
            </b-avatar>
            <div class="ml-1">
              <h4 class="mb-0">
                {{ getProjectCountByStatus('completed') }}
              </h4>
              <small class="text-muted">مكتملة</small>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Content -->
    <div
      v-if="loading && !projects.length"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="جاري التحميل..."
      />
    </div>

    <div v-else>
      <!-- Kanban View -->
      <project-assignments-kanban-view
        :projects="projects"
        @refresh="refreshProjects"
        @project-updated="handleProjectUpdate"
        @project-assigned="handleProjectAssigned"
      />
    </div>

    <!-- Select Projects Modal -->
    <b-modal
      id="select-projects-modal"
      v-model="showSelectProjectsModal"
      title="اختيار المشاريع"
      size="lg"
      ok-title="اختيار"
      cancel-title="إلغاء"
      @ok="handleSelectProjects"
      @cancel="resetSelectProjectsForm"
    >
      <div
        v-if="loadingAvailableProjects"
        class="text-center py-3"
      >
        <b-spinner
          variant="primary"
          label="جاري التحميل..."
        />
      </div>
      <div v-else>
        <b-form-group label="المشاريع المتاحة">
          <v-select
            v-model="selectedProjectIds"
            :options="availableProjects"
            :reduce="val => val.id"
            :get-option-label="option => getProjectDisplayName(option)"
            multiple
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            placeholder="اختر المشاريع"
          >
            <template #option="option">
              <div>
                <strong>{{ option.name || option.surveying_code || option.project_number || `مشروع #${option.id}` }}</strong>
                <span
                  v-if="option.surveying_code || option.project_number"
                  class="text-muted ml-1"
                >
                  ({{ option.surveying_code ? option.surveying_code : option.project_number }})
                </span>
              </div>
            </template>
            <template #selected-option="option">
              <span>{{ getProjectDisplayName(option) }}</span>
            </template>
          </v-select>
        </b-form-group>
        <b-alert
          variant="info"
          show
          class="mt-2"
        >
          <small>يمكنك اختيار عدة مشاريع في نفس الوقت</small>
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BAvatar,
  BModal,
  BFormGroup,
  BAlert,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ProjectAssignmentsKanbanView from './ProjectAssignmentsKanbanView.vue'

export default {
  name: 'ProjectAssignmentsPage',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAvatar,
    BModal,
    BFormGroup,
    BAlert,
    ProjectAssignmentsKanbanView,
  },
  data() {
    return {
      loading: false,
      loadingAvailableProjects: false,
      projects: [],
      availableProjects: [],
      showSelectProjectsModal: false,
      selectedProjectIds: [],
      // Track selected projects locally since there's no GET assigned projects endpoint
      selectedProjectsMap: {},
    }
  },
  watch: {
    showSelectProjectsModal(newVal) {
      if (newVal) {
        this.loadAvailableProjects()
      }
    },
  },
  mounted() {
    this.loadProjects()
  },
  methods: {
    async loadProjects() {
      this.loading = true
      try {
        // Use getAvailableProjects to get projects
        const response = await this.$store.dispatch('projects/getAvailableProjects')

        // Handle response structure according to API guide
        // Response: { success: true, data: [projects] }
        if (response && response.data) {
          let projectsList = []

          if (Array.isArray(response.data)) {
            projectsList = response.data
          } else if (response.data.projects && Array.isArray(response.data.projects)) {
            projectsList = response.data.projects
          }

          // Process projects - check if they have assignment info
          // Projects from getAvailableProjects might include both available and assigned projects
          this.projects = projectsList.map(project => {
            // If project has assignment info from API, use it
            if (project.assignment) {
              return {
                ...project,
                assignment: project.assignment,
              }
            }
            // Check local tracking for status updates
            if (this.selectedProjectsMap[project.id]) {
              return {
                ...project,
                assignment: this.selectedProjectsMap[project.id].assignment,
              }
            }
            // If no assignment, create pending assignment for display
            // (This handles projects that were just selected)
            return {
              ...project,
              assignment: {
                status: 'pending',
                project_id: project.id,
              },
            }
          }).filter(project =>
            // Only show projects that have assignment status (assigned projects)
            project.assignment && project.assignment.status)
        } else {
          this.projects = []
        }

        // Merge with localStorage for any local status updates
        const storedProjects = localStorage.getItem('projectAssignments')
        if (storedProjects) {
          try {
            const parsed = JSON.parse(storedProjects)
            // Update projects with localStorage data if available
            this.projects = this.projects.map(project => {
              if (parsed[project.id] && parsed[project.id].assignment) {
                return {
                  ...project,
                  assignment: parsed[project.id].assignment,
                }
              }
              return project
            })
            // Also add any projects from localStorage that might not be in API response
            Object.keys(parsed).forEach(projectId => {
              const storedProject = parsed[projectId]
              if (!this.projects.find(p => p.id === parseInt(projectId))) {
                this.projects.push(storedProject)
              }
            })
          } catch (e) {
            console.error('Error parsing stored projects:', e)
          }
        }
      } catch (error) {
        console.error('Error loading projects:', error)
        const errorMessage = this.handleApiError(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطأ',
            icon: 'XIcon',
            variant: 'danger',
            text: errorMessage,
          },
        })
        this.projects = []
      } finally {
        this.loading = false
      }
    },
    async loadAvailableProjects() {
      this.loadingAvailableProjects = true
      try {
        const response = await this.$store.dispatch('projects/getAvailableProjects')
        if (response && response.data) {
          this.availableProjects = Array.isArray(response.data)
            ? response.data
            : response.data.projects || []
        } else {
          this.availableProjects = []
        }
      } catch (error) {
        console.error('Error loading available projects:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطأ',
            icon: 'XIcon',
            variant: 'danger',
            text: 'فشل تحميل المشاريع المتاحة',
          },
        })
        this.availableProjects = []
      } finally {
        this.loadingAvailableProjects = false
      }
    },
    async handleSelectProjects() {
      if (!this.selectedProjectIds || this.selectedProjectIds.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'تحذير',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'يرجى اختيار مشروع واحد على الأقل',
          },
        })
        return
      }

      try {
        const result = await this.$store.dispatch('projects/selectProjects', {
          projectIds: this.selectedProjectIds,
        })

        const assignedCount = (result && result.data && result.data.assigned_count) || (result && result.assigned_count) || 0
        const skippedCount = (result && result.data && result.data.skipped_count) || (result && result.skipped_count) || 0

        // Add selected projects to local tracking
        // Find the selected projects from availableProjects and add them with pending status
        this.selectedProjectIds.forEach(projectId => {
          const project = this.availableProjects.find(p => p.id === projectId)
          if (project && !this.selectedProjectsMap[projectId]) {
            // Add project with assignment info
            this.selectedProjectsMap[projectId] = {
              ...project,
              assignment: {
                id: null, // Will be set by backend response if available
                project_id: projectId,
                employee_id: null,
                status: 'pending', // Newly selected projects start as pending
              },
            }
          }
        })

        // Save to localStorage for persistence
        localStorage.setItem('projectAssignments', JSON.stringify(this.selectedProjectsMap))

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'نجح',
            icon: 'CheckIcon',
            variant: 'success',
            text: `تم اختيار ${assignedCount} مشروع بنجاح${skippedCount > 0 ? ` (تم تخطي ${skippedCount})` : ''}`,
          },
        })

        this.showSelectProjectsModal = false
        this.resetSelectProjectsForm()

        // Refresh the projects list
        this.loadProjects()
      } catch (error) {
        const errorMessage = this.handleApiError(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطأ',
            icon: 'XIcon',
            variant: 'danger',
            text: errorMessage,
          },
        })
      }
    },
    resetSelectProjectsForm() {
      this.selectedProjectIds = []
    },
    refreshProjects() {
      this.loadProjects()
    },
    handleProjectUpdate(project) {
      // Update project in local tracking when status changes (without showing spinner)
      if (project && project.id) {
        this.selectedProjectsMap[project.id] = project
        localStorage.setItem('projectAssignments', JSON.stringify(this.selectedProjectsMap))

        // Update the projects array without triggering a full reload
        const projectIndex = this.projects.findIndex(p => p.id === project.id)
        if (projectIndex !== -1) {
          // Update existing project in place
          this.$set(this.projects, projectIndex, project)
        } else {
          // Add new project if not found
          this.projects.push(project)
        }
      }
    },
    handleProjectAssigned(project) {
      // Remove project from the list after it's assigned to an employee
      if (project && project.id) {
        // Remove from projects array
        const projectIndex = this.projects.findIndex(p => p.id === project.id)
        if (projectIndex !== -1) {
          this.projects.splice(projectIndex, 1)
        }

        // Remove from local tracking
        if (this.selectedProjectsMap[project.id]) {
          delete this.selectedProjectsMap[project.id]
          localStorage.setItem('projectAssignments', JSON.stringify(this.selectedProjectsMap))
        }
      }
    },
    getProjectCountByStatus(status) {
      if (!this.projects || !Array.isArray(this.projects)) {
        return 0
      }
      return this.projects.filter(project => {
        const assignmentStatus = (project.assignment && project.assignment.status) || project.status
        return assignmentStatus === status
      }).length
    },
    getProjectDisplayName(project) {
      // Display project name with project_number if available
      if (project.name && (project.surveying_code || project.project_number)) {
        return `${project.name} (${project.surveying_code ? project.surveying_code : project.project_number})`
      }
      return project.name || project.surveying_code || project.project_number || project.order_code || `مشروع #${project.id}`
    },
    handleApiError(error) {
      // Error handler utility as per the guide
      if (!error || !error.response) {
        return 'خطأ في الاتصال بالخادم'
      }

      const messages = {
        employee_not_found: 'الموظف غير موجود',
        project_not_assigned_to_employee: 'المشروع غير مخصص لك',
        project_must_be_in_progress: 'المشروع يجب أن يكون قيد العمل',
        project_must_be_in_review: 'المشروع يجب أن يكون قيد المراجعة',
      }

      const message = error.response.data && error.response.data.message
      return messages[message] || (error.response.data && error.response.data.message) || 'حدث خطأ غير متوقع'
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/app-tasks.scss';
</style>

<style lang="scss" scoped>
.project-assignments-page {
  .rotate-animation {
    animation: rotate 1s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
